import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Button, ErrorState, Skeleton, TextField } from '@shared/design-system/ui'
import { AppError } from '@shared/api/error'
import { toast } from '@shared/lib/toast/toast-store'
import {
  useChangeMemberPassword,
  useMemberMeQuery,
  usePatchMemberMe,
  useUploadMemberAvatar,
} from '../api/member-access.queries'
import { useMemberSession } from '../model/member-session-store'
import { MemberAccessLayout } from './MemberAccessLayout'
import {
  accountActions,
  accountAvatar,
  accountAvatarFallback,
  accountPhoto,
  form,
  loginPanel,
  logoutButton,
  muted,
  passwordBackdrop,
  passwordPanel,
  passwordTitle,
  subtitle,
  title,
} from './member-access.css'

const AVATAR_MAX_BYTES = 5 * 1024 * 1024

function initials(name: string) {
  const words = name.trim().split(/\s+/).filter(Boolean).slice(0, 2)
  return words.map((word) => word[0]?.toUpperCase() ?? '').join('') || 'VP'
}

export function MemberAccountPage({ producerId }: { producerId: string }) {
  const navigate = useNavigate()
  const token = useMemberSession((state) => state.token)
  const setSession = useMemberSession((state) => state.setSession)
  const clear = useMemberSession((state) => state.clear)
  const session = useMemberSession()
  const me = useMemberMeQuery(Boolean(token))
  const patchName = usePatchMemberMe()
  const changePassword = useChangeMemberPassword()
  const uploadAvatar = useUploadMemberAvatar()
  const fileRef = useRef<HTMLInputElement>(null)
  const serverName = me.data?.name ?? ''
  const [nameDraft, setNameDraft] = useState<{ source: string; value: string } | null>(null)
  const name = nameDraft?.source === serverName ? nameDraft.value : serverName
  const [passwordOpen, setPasswordOpen] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [password, setPassword] = useState('')

  function setName(value: string) {
    setNameDraft({ source: serverName, value })
  }

  const closePassword = useCallback(() => {
    setPasswordOpen(false)
    setCurrentPassword('')
    setPassword('')
  }, [])

  if (!token) {
    void navigate({ to: '/acesso/$producerId', params: { producerId } })
    return null
  }

  if (me.isPending && !me.data) {
    return (
      <MemberAccessLayout producerName="Área de membros" catalogBackProducerId={producerId}>
        <Skeleton height={280} radius="16px" />
      </MemberAccessLayout>
    )
  }

  if (me.isError || !me.data) {
    return (
      <MemberAccessLayout producerName="Área de membros" catalogBackProducerId={producerId}>
        <ErrorState
          title="Não foi possível abrir sua conta"
          description="Tente de novo a partir do catálogo."
          onRetry={() => void me.refetch()}
        />
      </MemberAccessLayout>
    )
  }

  const profile = me.data

  return (
    <MemberAccessLayout producerName={profile.producerName} catalogBackProducerId={producerId}>
      <section className={loginPanel} data-testid="member-account">
        <div>
          <h1 className={title}>Sua conta</h1>
          <p className={subtitle}>Foto e nome desta área de membros.</p>
        </div>
        <div className={accountPhoto}>
          {profile.avatarUrl ? (
            <img className={accountAvatar} src={profile.avatarUrl} alt="" />
          ) : (
            <span className={accountAvatarFallback} aria-hidden>
              {initials(profile.name)}
            </span>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,.jpg,.jpeg,.png"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0]
              event.target.value = ''
              if (!file) return
              if (file.size > AVATAR_MAX_BYTES) {
                toast.error({ title: 'Foto grande demais', description: 'Envie uma imagem de até 5 MB.' })
                return
              }
              if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
                toast.error({ title: 'Formato nao suportado', description: 'Envie a foto em JPEG ou PNG.' })
                return
              }
              void uploadAvatar.mutateAsync(file).then(
                () => toast.success('Foto atualizada.'),
                (error: unknown) =>
                  toast.error({
                    title: 'Nao foi possivel enviar a foto',
                    description: error instanceof AppError ? error.message : 'Tente de novo.',
                  }),
              )
            }}
          />
          <Button
            type="button"
            variant="secondary"
            disabled={uploadAvatar.isPending}
            onClick={() => fileRef.current?.click()}
          >
            {uploadAvatar.isPending ? 'Enviando…' : 'Alterar foto'}
          </Button>
        </div>
        <form
          className={form}
          onSubmit={(event) => {
            event.preventDefault()
            if (name.trim() === profile.name.trim() || !name.trim()) return
            void patchName.mutateAsync(name).then(
              (updated) => {
                if (session.token && session.member && session.producerName) {
                  setSession(session.token, { ...session.member, name: updated.name }, session.producerName)
                }
                toast.success('Nome salvo.')
              },
              (error: unknown) =>
                toast.error({
                  title: 'Nao foi possivel salvar o nome',
                  description: error instanceof AppError ? error.message : 'Tente de novo.',
                }),
            )
          }}
        >
          <TextField label="Nome" value={name} onChange={(event) => setName(event.target.value)} required />
          <TextField label="E-mail" type="email" value={profile.email} disabled />
          <p className={muted}>O e-mail é o login desta área e não muda.</p>
          {name.trim() !== profile.name.trim() ? (
            <Button type="submit" fullWidth disabled={patchName.isPending || !name.trim()}>
              Salvar nome
            </Button>
          ) : null}
        </form>
        <div className={accountActions}>
          <Button type="button" variant="secondary" fullWidth onClick={() => setPasswordOpen(true)}>
            Mudar senha
          </Button>
          <button type="button" className={logoutButton} onClick={() => clear()}>
            Sair da conta
          </button>
        </div>
      </section>
      {passwordOpen ? (
        <ChangePasswordDialog
          pending={changePassword.isPending}
          currentPassword={currentPassword}
          password={password}
          onCurrentPassword={setCurrentPassword}
          onPassword={setPassword}
          onClose={closePassword}
          onSubmit={() => {
            void changePassword.mutateAsync({ currentPassword, password }).then(
              () => {
                closePassword()
                toast.success('Senha atualizada.')
              },
              (error: unknown) =>
                toast.error({
                  title: 'Nao foi possivel alterar a senha',
                  description: error instanceof AppError ? error.message : 'Confira a senha atual.',
                }),
            )
          }}
        />
      ) : null}
    </MemberAccessLayout>
  )
}

function ChangePasswordDialog({
  pending,
  currentPassword,
  password,
  onCurrentPassword,
  onPassword,
  onClose,
  onSubmit,
}: {
  pending: boolean
  currentPassword: string
  password: string
  onCurrentPassword: (value: string) => void
  onPassword: (value: string) => void
  onClose: () => void
  onSubmit: () => void
}) {
  const titleId = useId()
  const panelRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const previousActive = document.activeElement instanceof HTMLElement ? document.activeElement : null
    panelRef.current?.querySelector<HTMLInputElement>('input')?.focus()

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && !pending) onClose()
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      previousActive?.focus()
    }
  }, [onClose, pending])

  return (
    <div
      className={passwordBackdrop}
      role="presentation"
      onClick={() => {
        if (!pending) onClose()
      }}
    >
      <section
        ref={panelRef}
        className={passwordPanel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
      >
        <div>
          <h2 id={titleId} className={passwordTitle}>
            Mudar senha
          </h2>
          <p className={subtitle}>Informe a senha atual e a nova senha.</p>
        </div>
        <form
          className={form}
          onSubmit={(event) => {
            event.preventDefault()
            onSubmit()
          }}
        >
          <TextField
            label="Senha atual"
            type="password"
            value={currentPassword}
            onChange={(event) => onCurrentPassword(event.target.value)}
            required
          />
          <TextField
            label="Nova senha"
            type="password"
            value={password}
            onChange={(event) => onPassword(event.target.value)}
            required
            minLength={8}
          />
          <Button type="submit" fullWidth disabled={pending}>
            {pending ? 'Salvando…' : 'Salvar senha'}
          </Button>
          <Button type="button" variant="secondary" fullWidth disabled={pending} onClick={onClose}>
            Cancelar
          </Button>
        </form>
      </section>
    </div>
  )
}
